import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChatCircleText,
  CheckCircle,
  Clock,
  Trash,
  Warning,
} from '@phosphor-icons/react';

import {
  FEEDBACK_ACCEPT_ATTR,
  FEEDBACK_ACCEPTED_TYPES,
  FEEDBACK_MAX_ATTACHMENT_BYTES,
  FEEDBACK_MAX_ATTACHMENTS,
  listFeedback,
  submitFeedback,
  withdrawFeedback,
  type FeedbackKind,
  type FeedbackStatus,
  type FeedbackTicket,
} from '@/api/feedback';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileDropzone } from '@/components/ui/file-dropzone';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useConfirm } from '@/components/ConfirmDialog';
import { startReplay } from '@/lib/sentry';

// Feedback page: a one-screen surface where a user can submit a new feedback
// ticket and review their previously submitted ones. Strict self-only — the
// backend filters on user_id; even an org admin sees only their own tickets
// here.
//
// On mount the page calls startReplay() to opt the user into a Sentry session
// replay so the bundle attached to the next "Send" includes context. Has no
// effect when Sentry is disabled (dev) or when the user already has a replay
// going (idempotent).

const TITLE_MIN = 4;
const TITLE_MAX = 120;
const BODY_MIN = 20;
const BODY_MAX = 4000;

export function Feedback() {
  const { slug = '' } = useParams();
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [kind, setKind] = useState<FeedbackKind | ''>('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Kick off a Sentry replay session as soon as the page mounts so the
  // user's last several seconds of UI activity get captured. Cheap if
  // Sentry isn't configured.
  useEffect(() => {
    startReplay();
  }, []);

  const ticketsQuery = useQuery({
    queryKey: ['feedback', slug],
    queryFn: () => listFeedback(token, slug),
    enabled: !!token && !!slug,
  });

  const submitMutation = useMutation({
    mutationFn: () => submitFeedback(token, slug, { title, body, kind, attachments }),
    onSuccess: () => {
      setTitle('');
      setBody('');
      setKind('');
      setAttachments([]);
      setValidationError(null);
      void queryClient.invalidateQueries({ queryKey: ['feedback', slug] });
    },
    onError: (err: Error) => {
      setValidationError(err.message || 'unknown');
    },
  });

  const withdrawMutation = useMutation({
    mutationFn: (ticketID: string) => withdrawFeedback(token, slug, ticketID),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['feedback', slug] });
    },
  });

  function clientSideValidate(): string | null {
    const t1 = title.trim();
    const b1 = body.trim();
    if (t1.length < TITLE_MIN || t1.length > TITLE_MAX) {
      return t('settings.feedback.form.titleLengthError', {
        min: TITLE_MIN,
        max: TITLE_MAX,
      });
    }
    if (b1.length < BODY_MIN || b1.length > BODY_MAX) {
      return t('settings.feedback.form.bodyLengthError', {
        min: BODY_MIN,
        max: BODY_MAX,
      });
    }
    return null;
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const err = clientSideValidate();
    if (err) {
      setValidationError(err);
      return;
    }
    setValidationError(null);
    submitMutation.mutate();
  }

  async function onWithdraw(ticket: FeedbackTicket) {
    const ok = await confirm({
      title: t('settings.feedback.withdraw.title'),
      body: t('settings.feedback.withdraw.body', { title: ticket.title }),
      confirmLabel: t('settings.feedback.withdraw.confirm'),
      tone: 'destructive',
    });
    if (!ok) return;
    withdrawMutation.mutate(ticket.id);
  }

  return (
    <div className="mx-auto max-w-2xl space-y-8 p-4 sm:p-6">
      <header className="space-y-1">
        <div className="flex items-center gap-2 text-foreground">
          <ChatCircleText weight="bold" size={24} />
          <h1 className="text-2xl font-bold tracking-tight">
            {t('settings.feedback.title')}
          </h1>
        </div>
        <p className="text-sm text-foreground/70">
          {t('settings.feedback.subtitle')}
        </p>
      </header>

      <form
        onSubmit={onSubmit}
        className="space-y-3 rounded-3xl border border-border/60 bg-card p-5 shadow-sm"
      >
        <div className="space-y-1">
          <label
            htmlFor="feedback-title"
            className="text-sm font-semibold text-foreground"
          >
            {t('settings.feedback.form.titleLabel')}
          </label>
          <input
            id="feedback-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={TITLE_MAX}
            placeholder={t('settings.feedback.form.titlePlaceholder')}
            className="w-full rounded-pill border border-border/60 bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-lime"
          />
        </div>

        <div className="space-y-1">
          <label
            htmlFor="feedback-kind"
            className="text-sm font-semibold text-foreground"
          >
            {t('settings.feedback.form.kindLabel')}
          </label>
          {/* Radix Select forbids an empty SelectItem value, so we use the
              'auto' sentinel for the no-selection / classifier-decides path
              and translate back to '' on the wire. */}
          <Select
            value={kind === '' ? 'auto' : kind}
            onValueChange={(v) => setKind(v === 'auto' ? '' : (v as FeedbackKind))}
          >
            <SelectTrigger id="feedback-kind">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">{t('settings.feedback.form.kindAuto')}</SelectItem>
              <SelectItem value="bug">{t('settings.feedback.kind.bug')}</SelectItem>
              <SelectItem value="feature_request">
                {t('settings.feedback.kind.feature_request')}
              </SelectItem>
              <SelectItem value="question">{t('settings.feedback.kind.question')}</SelectItem>
              <SelectItem value="other">{t('settings.feedback.kind.other')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <label
            htmlFor="feedback-body"
            className="text-sm font-semibold text-foreground"
          >
            {t('settings.feedback.form.bodyLabel')}
          </label>
          <textarea
            id="feedback-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={BODY_MAX}
            rows={6}
            placeholder={t('settings.feedback.form.bodyPlaceholder')}
            className="w-full rounded-2xl border border-border/60 bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-lime"
          />
          <p className="text-xs text-foreground/60">
            {t('settings.feedback.form.privacyNote')}
          </p>
        </div>

        <div className="space-y-1">
          <span className="text-sm font-semibold text-foreground">
            {t('settings.feedback.form.attachmentsLabel')}
          </span>
          <FileDropzone
            value={attachments}
            onChange={setAttachments}
            accept={FEEDBACK_ACCEPT_ATTR}
            acceptedMimeTypes={FEEDBACK_ACCEPTED_TYPES}
            maxFiles={FEEDBACK_MAX_ATTACHMENTS}
            maxBytes={FEEDBACK_MAX_ATTACHMENT_BYTES}
            cta={t('settings.feedback.form.attachmentsCta')}
            disabled={submitMutation.isPending}
          />
        </div>

        {validationError && (
          <p
            role="alert"
            className="rounded-2xl border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {validationError}
          </p>
        )}

        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={submitMutation.isPending || !title.trim() || !body.trim()}
          >
            {submitMutation.isPending
              ? t('settings.feedback.form.submitting')
              : t('settings.feedback.form.submit')}
          </Button>
        </div>
      </form>

      <section className="space-y-3">
        <h2 className="text-base font-semibold tracking-tight text-foreground">
          {t('settings.feedback.list.title')}
        </h2>
        {ticketsQuery.isLoading && (
          <p className="text-sm text-foreground/60">
            {t('settings.feedback.list.loading')}
          </p>
        )}
        {ticketsQuery.data?.tickets.length === 0 && (
          <p className="rounded-2xl border border-border/60 bg-card p-6 text-center text-sm text-foreground/60">
            {t('settings.feedback.list.empty')}
          </p>
        )}
        <ul className="space-y-2">
          {ticketsQuery.data?.tickets.map((ticket) => (
            <li
              key={ticket.id}
              className="rounded-2xl border border-border/60 bg-card p-4 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge status={ticket.status} />
                    {ticket.kind && (
                      <Badge variant="outline" className="text-xs">
                        {t(`settings.feedback.kind.${ticket.kind}`, ticket.kind)}
                      </Badge>
                    )}
                    {ticket.severity && (
                      <Badge variant="outline" className="text-xs">
                        {t(`settings.feedback.severity.${ticket.severity}`, ticket.severity)}
                      </Badge>
                    )}
                  </div>
                  <Link
                    to={`/orgs/${slug}/settings/feedback/${ticket.id}`}
                    className="block break-words text-sm font-semibold text-foreground hover:underline"
                  >
                    {ticket.title}
                  </Link>
                  <p className="text-xs text-foreground/60">
                    {new Date(ticket.created_at).toLocaleString()}
                    {ticket.github_issue_number !== undefined && ticket.github_issue_number !== null && (
                      <> · #{ticket.github_issue_number}</>
                    )}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {ticket.status !== 'closed' && (
                    <button
                      type="button"
                      onClick={() => onWithdraw(ticket)}
                      className="inline-flex h-9 items-center gap-1 rounded-pill border border-destructive/40 px-3 text-xs font-semibold text-destructive hover:bg-destructive/10"
                      disabled={withdrawMutation.isPending}
                    >
                      <Trash size={14} weight="bold" />
                      {t('settings.feedback.list.withdraw')}
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function StatusBadge({ status }: { status: FeedbackStatus }) {
  const { t } = useTranslation('common');
  const cfg: Record<
    FeedbackStatus,
    { icon: typeof CheckCircle; tone: 'default' | 'success' | 'warning' | 'destructive' }
  > = {
    pending: { icon: Clock, tone: 'default' },
    submitted: { icon: CheckCircle, tone: 'success' },
    open: { icon: ChatCircleText, tone: 'success' },
    closed: { icon: CheckCircle, tone: 'default' },
    failed: { icon: Warning, tone: 'destructive' },
  };
  const { icon: Icon, tone } = cfg[status];
  const toneClass = (() => {
    switch (tone) {
      case 'success':
        return 'border-lime/40 bg-lime/10 text-foreground';
      case 'warning':
        return 'border-orange-400/40 bg-orange-400/10 text-orange-700';
      case 'destructive':
        return 'border-destructive/40 bg-destructive/10 text-destructive';
      default:
        return 'border-border/60 bg-muted text-foreground/80';
    }
  })();
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-pill border px-2 py-0.5 text-xs font-semibold ${toneClass}`}
    >
      <Icon size={12} weight="bold" />
      {t(`settings.feedback.status.${status}`)}
    </span>
  );
}
